/* eslint-disable import/prefer-default-export */
import { useEffect, useCallback, useRef } from 'react';
import { find, isEmpty } from 'lodash';
import { useSelector } from 'react-redux';

import { useShippingCosts } from './useShippingCosts';

/**
 * Hook para gestionar el método de entrega de una orden
 * Responsabilidad única: Lógica de selección del método de entrega y valores asociados
 */
let executeCustomLogic = false;

export const useOrderDeliveryMethod = ({
  useWatch, getValues, setValue, control
}) => {
  const { retailer_info } = useSelector(
    (state) => state.retailerUsersReducer
  );

  const {
    shipping_cost_method,
    shop_active_send_order,
    shop_active_store_pickup
  } = retailer_info;

  const deliveryMethod = useWatch({ name: 'delivery_method', control });
  const shipping_cost_id = useWatch({ name: 'shipping_cost_id', control });

  // Track previous shipping_cost_id to detect actual changes
  const prevShippingCostIdRef = useRef(shipping_cost_id);

  // Usar el hook separado para los costos de envío
  const { shippingCostOptions, loadingShippingCosts } = useShippingCosts(shipping_cost_method);

  // Limpiar campos cuando cambia el método de entrega
  useEffect(() => {
    if (!deliveryMethod || deliveryMethod === 'send_order') return;

    // Use setValue instead of reset to avoid triggering full form re-render
    setValue('address', '');
    setValue('city', '');
    setValue('zip_code', '');
    setValue('country_id', '');
    setValue('shipping_value', '');
    setValue('shipping_cost_id', '');
  }, [deliveryMethod, setValue]);

  useEffect(() => {
    // Solo ejecutar si shipping_cost_id realmente cambió
    if (prevShippingCostIdRef.current === shipping_cost_id || !executeCustomLogic) {
      return;
    }

    if (shipping_cost_id === 'custom') {
      setValue('shipping_value', '');
    } else if (shipping_cost_id !== undefined && shipping_cost_id !== null && shipping_cost_id !== '') {
      const fixCost = find(shippingCostOptions, { id: String(shipping_cost_id) });
      // Solo setear si el costo existe y tiene valor
      if (fixCost && fixCost.cost) {
        setValue('shipping_value', String(fixCost.cost));
      }
    }

    prevShippingCostIdRef.current = shipping_cost_id;
  }, [shipping_cost_id, shippingCostOptions, setValue]);

  // Función para establecer costo de envío
  const setShippingCost = useCallback((costId, value) => {
    executeCustomLogic = false;

    prevShippingCostIdRef.current = costId;
    setValue('filledShippingCostManually', true);
    setValue('shipping_cost_id', String(costId));
    setValue('shipping_value', value);

    setTimeout(() => {
      executeCustomLogic = true;
    }, 1000);
  }, [setValue]);

  // Aplicar costo de envío basado en método y opciones disponibles
  useEffect(() => {
    // Esperar a que shippingCostOptions esté cargado
    if (!deliveryMethod || isEmpty(shippingCostOptions) || loadingShippingCosts) return;

    if (deliveryMethod !== 'send_order') return;

    if (getValues('filledShippingCostManually')) return;

    const orderFormData = getValues();

    if (shipping_cost_method === 'fixed_cost') {
      const fixCost = find(shippingCostOptions, { shipping_type: 'fixed_cost' });
      if (fixCost && fixCost.cost) {
        if (!orderFormData.id) {
          setShippingCost('', fixCost.cost);
        } else {
          setShippingCost('', orderFormData.shipping_value);
        }
      }
      return;
    }

    if (shipping_cost_method === 'location_cost') {
      const fixCost = find(shippingCostOptions, { id: String(shipping_cost_id) });
      if (fixCost && fixCost.cost) {
        if (!orderFormData.id) {
          setShippingCost(fixCost.value, fixCost.cost);
        } else {
          setShippingCost(fixCost.value, orderFormData.shipping_value || fixCost.cost);
        }
      } else if (orderFormData.id && Number(orderFormData.shipping_value) > 0) {
        setShippingCost('custom', orderFormData.shipping_value || '');
      }
    }
  }, [shippingCostOptions, loadingShippingCosts, deliveryMethod, shipping_cost_id, shipping_cost_method, getValues, setShippingCost]);

  const canShowDelivery = shop_active_send_order || shop_active_store_pickup;

  return {
    shop_active_send_order,
    shop_active_store_pickup,
    shipping_cost_method,
    setValue,
    getValues,
    retailer_info,
    deliveryMethod,
    shipping_cost_id,
    shippingCostOptions,
    loadingShippingCosts,
    canShowDelivery
  };
};
